// ===========================================
// Admin Settings Types (Frontend)
// Phase 2 Priority 3 - Admin Settings Enhancements
// ===========================================

// Type-only import: keeps the runtime `REPORT_KEYS` array out of this module
// while letting `ReportsSettings.visibleCards` be the narrow `ReportKey[]`.
import type { ReportKey } from '../constants/reportKeys.constants';

// ===========================================
// INVOICE SETTINGS
// ===========================================

export type TaxType = 'NONE' | 'GST' | 'VAT' | 'CUSTOM';
export type TaxMode = 'INCLUDED' | 'EXCLUDED' | 'NONE';
export type LineItemGrouping = 'BY_CLASS' | 'BY_PROGRAM_LEVEL';

export interface InvoiceSettings {
  prefix: string | null;
  taxType: TaxType;
  taxName: string | null;
  taxRate: number;
  taxMode: TaxMode;
  taxNumber: string | null;
  defaultDueDays: number;
  // Payment reminders v2 (#372): the ladder is a list of STEPS (see ReminderStep), no longer flat keys here.
  /** Where families ask about a payment plan; null = the school email (reminders only) */
  billingContactEmail: string | null;
  footerText: string | null;
  invoiceLineItemGrouping: LineItemGrouping;
  // Late fees (#372). Mirrors the backend type field for field. `lateFeeEnabled` in a RESPONSE is
  // the EFFECTIVE state (the switch and the server's start date), never the raw JSON.
  lateFeeEnabled: boolean;
  lateFeeType: LateFeeType;
  lateFeeAmount: number | null;
  lateFeePercent: number | null;
  /** PERCENT only. */
  lateFeePerFeeCap: number | null;
  lateFeeFirstDays: number;
  lateFeeRepeatEnabled: boolean;
  lateFeeRepeatFirstDays: number;
  lateFeeRepeatEveryDays: number;
  lateFeeMaxCount: number | null;
  lateFeeMaxTotal: number | null;
  lateFeeMinOwing: number | null;
  lateFeeChargePartPaid: boolean;
  lateFeeTaxable: boolean;
  lateFeeChargeTuition: boolean;
  lateFeeChargeProducts: boolean;
  lateFeeChargeOther: boolean;
  lateFeeLateInvoiceGapDays: number;
  lateFeeCopyBillingContact: boolean;
}

export type LateFeeType = 'FIXED' | 'PERCENT';

/** #372: what a save did to late fees, decided by the SERVER (services/lateFees/policyChange.ts). */
export type LateFeeChangeKind = 'ENABLED' | 'DISABLED' | 'STRICTER' | 'LOOSER' | 'NONE';
export type StricterReason =
  | 'DAYS_FEWER'
  | 'AMOUNT_HIGHER'
  | 'PERCENT_HIGHER'
  | 'CAP_HIGHER'
  | 'CAP_REMOVED'
  | 'TYPE_CHANGED'
  | 'PART_PAID_ON'
  | 'TAX_ON'
  | 'REPEAT_ON'
  | 'REPEAT_SOONER'
  | 'MAX_COUNT_HIGHER'
  | 'MAX_COUNT_REMOVED'
  | 'MAX_TOTAL_HIGHER'
  | 'MAX_TOTAL_REMOVED'
  | 'MIN_OWING_LOWER'
  | 'MIN_OWING_REMOVED'
  | 'KIND_ADDED'
  | 'GAP_SHORTER';

export interface LateFeeChange {
  kind: LateFeeChangeKind;
  reasons: StricterReason[];
  /** The start date after the save (YYYY-MM-DD), or null when fees are off. */
  enabledFrom: string | null;
}

// ----- Reminder steps (#372 slice B) -----------------------------------------------------------
// Mirrors apps/backend/src/services/invoiceReminders/steps (ReminderStepView). ASSERTED, not validated.

export type ReminderStepKind = 'BEFORE_DUE' | 'ON_DUE' | 'LAST_CHANCE' | 'OVERDUE';

export interface ReminderStep {
  id: string;
  kind: ReminderStepKind;
  name: string;
  /** < 0 before the due date, 0 on it, > 0 after. */
  dayOffset: number;
  /** OVERDUE only: repeat every N days. null = no repeat. */
  repeatEveryDays: number | null;
  /** null with a repeat = until paid. */
  maxRepeats: number | null;
  enabled: boolean;
  copyBillingContact: boolean;
  sortOrder: number;
  /** Which parts of the wording the school has changed (edited in System Emails). */
  customised: { subject: boolean; preheader: boolean; body: boolean };
}

/** GET /admin/settings/invoice/reminder-steps (steps are already in send order). */
export interface ReminderStepsResponse {
  steps: ReminderStep[];
  /** Are late fees on? The Last Chance step must then stay, before the first fee day. */
  lateFeesOn: boolean;
  lateFeeFirstDays: number;
  /** Is the automatic sweep live on the server? */
  remindersActive: boolean;
  lateFeesActive: boolean;
}

export interface ReminderStepCreateInput {
  kind: ReminderStepKind;
  name: string;
  dayOffset: number;
  repeatEveryDays?: number | null;
  maxRepeats?: number | null;
  enabled?: boolean;
  copyBillingContact?: boolean;
}

/** `kind` never changes after create. */
export type ReminderStepUpdateInput = Partial<Omit<ReminderStepCreateInput, 'kind'>>;

/** The ranges the server enforces (a typo check only; the server is the authority). */
export const REMINDER_STEP_DAY_RANGE: Record<ReminderStepKind, { min: number; max: number }> = {
  BEFORE_DUE: { min: -60, max: -1 },
  ON_DUE: { min: 0, max: 0 },
  LAST_CHANCE: { min: 1, max: 90 },
  OVERDUE: { min: 1, max: 90 },
};
export const REMINDER_REPEAT_EVERY_RANGE = { min: 1, max: 60 } as const;
export const REMINDER_MAX_REPEATS_RANGE = { min: 1, max: 52 } as const;
export const REMINDER_MAX_LIVE_STEPS = 10;

/** #372 slice C: why the Last Chance email would not support a late fee. */
export type LastChanceWarning = 'BODY_MISSING' | 'STEP_DISABLED' | 'STEP_TOO_LATE';

/**
 * What GET / PATCH / reset `/admin/settings/invoice` return: the settings plus whether the
 * automatic reminder sweep is live (INVOICE_REMINDERS_ENABLED on the server). Read-only.
 */
export interface InvoiceSettingsResponse extends InvoiceSettings {
  remindersActive: boolean;
  /** #372: may late fees be switched on at all (LATE_FEES_ENABLED on the server)? */
  lateFeesActive: boolean;
  /** #372: the school day fees count from (YYYY-MM-DD), or null when fees are off. */
  lateFeesEnabledFrom: string | null;
  /** #372 slice C: why the Last Chance email would not support a fee. Null unless fees are effectively on. */
  lastChanceWarning?: LastChanceWarning | null;
  /** #372: PATCH only. */
  lateFeeChange?: LateFeeChange;
}

// ===========================================
// COLOR PALETTE SETTINGS
// ===========================================

export interface ColorPaletteColor {
  value: string;
  label: string;
  textColor: string;
}

export interface ColorPaletteSettings {
  colors: ColorPaletteColor[];
  useDefaults: boolean;
}

// ===========================================
// ACADEMIC YEAR SETTINGS
// ===========================================

export type Hemisphere = 'northern' | 'southern' | 'custom';

export interface AcademicYearSettings {
  startMonth: number;
  hemisphere: Hemisphere;
}

// ===========================================
// ADMIN NAVIGATION SETTINGS
// ===========================================

export interface AdminNavigationSettings {
  order: string[];
  hidden: string[];
}

// ===========================================
// CALENDAR STATUS COLORS
// ===========================================

export interface CalendarStatusColors {
  hybridPlaceholder: string;
  hybridIndividual: string;
  hybridGroup: string;
  meetAndGreet: string;
}

// ===========================================
// ROLE COLORS SETTINGS
// ===========================================

export interface RoleColorEntry {
  background: string;
  text: string;
}

export interface RoleColorsSettings {
  ADMIN: RoleColorEntry;
  TEACHER: RoleColorEntry;
  PARENT: RoleColorEntry;
  STUDENT: RoleColorEntry;
}

// ===========================================
// SCHOOL INFO SETTINGS
// ===========================================

export interface SchoolInfoSettings {
  schoolName: string | null;
  description: string | null;
  /**
   * Short brand tagline, e.g. "Where Every Note Matters". Distinct from
   * `description`, which is directory prose — this is the line that sits under the
   * logo on a PDF letterhead and in the email footer. Mirrors the backend type.
   */
  tagline: string | null;
  contactNumber: string | null;
  logoUrl: string | null;
  domain: string | null;
  replyToEmail: string | null;
  timezone: string;
  requireWorkingWithChildrenId: boolean;
  mainLocationId: string | null;
  /** What the school calls its owner tier; null = the default. Read through `useOwnerTitle`. */
  ownerTitle?: string | null;
  /** The office's working days, JS day numbers (0 = Sunday ... 6 = Saturday); absent = Monday to Friday. */
  officeDays?: number[];
}

// ===========================================
// LEGAL SETTINGS
// ===========================================

export interface LegalSettings {
  termsOfService: string | null;
  privacyPolicy: string | null;
}

// ===========================================
// PAYMENT SETTINGS
// ===========================================

export type FeeMode = 'ABSORB' | 'PASS_TO_CUSTOMER';

export interface StripeProcessingRate {
  percentRate: number;
  fixedCents: number;
  currency: string;
  label: string;
}

export type StripeProcessingRates = Record<string, StripeProcessingRate>;

export interface PaymentSettings {
  feeMode: FeeMode;
  countryCode: string;
  customRate: { percentRate: number; fixedCents: number } | null;
}

export type PaymentSettingsInput = Partial<PaymentSettings>;

// ===========================================
// EXTRA LESSON SETTINGS
// ===========================================

export type BookingType = 'REQUEST' | 'DIRECT';

export interface ExtraLessonSettings {
  enabled: boolean;
  bookingType: BookingType;
  defaultDurationMinutes: number;
  maxRequestsPerStudentPerTerm: number | null;
  allowParentSelectTeacher: boolean;
  useTeacherAvailability: boolean;
}

export type ExtraLessonSettingsInput = Partial<ExtraLessonSettings>;

// ===========================================
// REPORTS SETTINGS (2026-09-16)
// ===========================================

/**
 * Which report cards render on the Reports Overview page.
 *
 * ⚠️ The 4-tile quick-stats banner is deliberately NOT configurable: it is the
 * at-a-glance summary and has no per-tile route.
 */
export interface ReportsSettings {
  /**
   * Report keys whose Overview card is visible (see constants/reportKeys).
   *
   * ⚠️ An EMPTY array is a deliberate choice ("hide every card"), NOT an absent
   * value — the Overview must render an explicit empty state rather than
   * falling back to showing everything.
   *
   * Typed `ReportKey[]`, not `string[]` (tightened at /qa 2026-09-16).
   */
  visibleCards: ReportKey[];
}

/**
 * ⚠️ NOT `Partial<ReportsSettings>`, unlike every sibling input type here.
 * The backend requires `visibleCards`: this is a PUT that replaces a complete
 * set of checkboxes, so "sent no key" and "unticked every box" must stay
 * distinguishable.
 */
export interface ReportsSettingsInput {
  visibleCards: ReportKey[];
}

// ===========================================
// REPORT SCHEDULES (Slice 2, 2026-09-16)
// ===========================================

export type ReportScheduleFrequency = 'DAILY' | 'WEEKLY' | 'MONTHLY';

export interface ReportScheduleRecipient {
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  /**
   * The picker only (roles slice 2): an Admin, or staff holding `reports.read`. A schedule that
   * includes Retro Edits goes to Admins only.
   */
  tier?: 'ADMIN' | 'STAFF';
}

export interface ReportSchedule {
  id: string;
  name: string;
  frequency: ReportScheduleFrequency;
  /** 0-23, in the SCHOOL's own timezone — not the viewer's. */
  sendHour: number;
  /**
   * WHICH DAY: weekly = day of week (0=Sun…6=Sat), monthly = day of month
   * (1-28), daily = ignored.
   * ⚠️ Monthly stops at 28 — 29-31 would silently skip shorter months.
   */
  sendDay: number;
  reportKeys: ReportKey[];
  includeSummary: boolean;
  isActive: boolean;
  lastSentAt: string | null;
  recipients: ReportScheduleRecipient[];
  createdAt: string;
  updatedAt: string;
  /**
   * An update only: stored recipients the server took off because they can no longer receive
   * reports (demoted, role removed, deactivated). The save names them (roles slice 2 step 6).
   */
  removedRecipients?: Array<{ userId: string; firstName: string; lastName: string }>;
}

/**
 * ⚠️ `reportKeys` and `recipientUserIds` REPLACE their whole set on update —
 * they are checkbox lists, so a partial merge would make "removed the last one"
 * unrepresentable. Same reasoning as ReportsSettingsInput.
 */
export interface ReportScheduleInput {
  name: string;
  frequency: ReportScheduleFrequency;
  sendHour: number;
  sendDay: number;
  reportKeys: ReportKey[];
  recipientUserIds: string[];
  includeSummary?: boolean;
  isActive?: boolean;
}

export type ReportScheduleUpdateInput = Partial<ReportScheduleInput>;

// ===========================================
// COMBINED SETTINGS
// ===========================================

export interface AllAdminSettings {
  /** null without operations_invoices.read (roles slice 1a) */
  invoiceSettings: InvoiceSettings | null;
  colorPalette: ColorPaletteSettings;
  academicYear: AcademicYearSettings;
  adminNavigation: AdminNavigationSettings;
  calendarStatusColors: CalendarStatusColors;
  roleColors: RoleColorsSettings;
  schoolInfo: SchoolInfoSettings;
  legalSettings: LegalSettings;
  /** null without operations_payments.read (roles slice 1a) */
  paymentSettings: PaymentSettings | null;
  extraLessonSettings: ExtraLessonSettings;
  reportsSettings: ReportsSettings;
}

// ===========================================
// INPUT TYPES (for mutations)
// ===========================================

export type InvoiceSettingsInput = Partial<InvoiceSettings>;
export type ColorPaletteInput = Partial<ColorPaletteSettings>;
export type AcademicYearInput = AcademicYearSettings;
export type AdminNavigationInput = Partial<AdminNavigationSettings>;
export type CalendarStatusColorsInput = Partial<CalendarStatusColors>;
export type RoleColorsInput = Partial<RoleColorsSettings>;
export type SchoolInfoInput = Partial<SchoolInfoSettings>;
export type LegalSettingsInput = Partial<LegalSettings>;

export interface AddColorInput {
  value: string;
  label: string;
  textColor?: string;
}

// ===========================================
// API RESPONSE TYPES
// ===========================================

export interface AdminSettingsResponse<T> {
  status: 'success';
  data: T;
  message?: string;
}
